import { IDropdownResponse } from 'src/app/shared/models/dynamic.model';
import { EntityPermissionFields } from 'src/app/shared/models/permission-flags.model';

export interface ComponentMapping {
  title: string;
  componentType: any;
}

export interface MawbColumnsData {
  mawb: number;
  /** PAC-24812 — true when MAWB is part of the international incoming flow */
  incomingInternational?: boolean;
  /** PAC-24812 — true when a published Manifest blocks deleting this record */
  isManifestPublished?: boolean;
  mawbId: number;
  origin: string;
  destination: string;
  agentCode: number;
  airlineCode: number;
  clearanceType: string;
  agentName: string;
  airlineName: string;
  mawbExt?: string;
  isPublished?: string | boolean;
  noOfManifests?: number;
  noOfHawbs?: number;
  linehaulCharge?: string | number;
  isAllowedToEdit?: EntityPermissionFields['isAllowedToEdit'];
  allowToEditMawbDate?: EntityPermissionFields['allowToEditMawbDate'];
}

/** Criteria for `PaginatedDataService` on MAWB summary (basic vs advance). */
export type MawbSummarySearchCriteria =
  | {
      variant: 'basic';
      searchText: string;
    }
  | { variant: 'advance'; payload?: any };

export interface ManifestGridColumns {
  manifestId: string;
  manifestNumber: string;
  date: string;
  bags: number;
  shipments: number;
  isInputRow: boolean;
  id?: any;
}

export interface FlightGridColumns {
  airlineCode: string;
  flightNumber: string;
  slacCount: string;
  slacWeight: string;
  arrivalDate: string;
  mawbPart: string;
  isInputRow: boolean;
}

export interface IMawbDetailsData {
  mawbId: number;
  mawbExt: string;
  originAirport: string;
  destinationAirport: string;
  airlineId: number;
  airlineName: string;
  information: string;
  receivingAgent: string;
  clearanceType: string;
  agentCode: number;
  agentName: string;
  clearanceTypeCode: string;
  destinationID: number;
  originID: number;
}

export interface IMawbDetailsStateModel {
  mawbInfo: IMawbDetailsData;
}

export const defaultMawbInfoData = (): IMawbDetailsData => ({
  mawbId: 0,
  mawbExt: '',
  originAirport: '',
  destinationAirport: '',
  airlineId: 0,
  airlineName: '',
  information: '',
  receivingAgent: '',
  clearanceType: '',
  agentCode: 0,
  agentName: '',
  clearanceTypeCode: '',
  destinationID: 0,
  originID: 0,
});

export interface IMawbMiscellaneousData {
  usCbpEccf86EntryNumber?: any;
  clearanceAgent?: string;
  linehaulCharge?: any;
  /** PAC-24713: read-only, populated from CBP via MAWB fetch. Never sent on save. */
  usCBPEntryNumbers?: string[];
}

/** PAC-24713: row returned by GET /v1/mawb/getEntryNumbers/:mawbId. */
export interface ICbpEntryNumber {
  shipment: string;
  pieceCount: number;
  entryNumber: string;
  totalValue: string;
}

export interface IMawbMiscellaneousStateModel {
  miscellaneous: IMawbMiscellaneousData;
}

export interface IMawbManifestData {
  id: string;
  noofpieces: string;
  noofbag: string;
  noofshipment: string;
  date: string;
  source?: string;
}
export interface IManifestStateModel {
  manifestList: IMawbManifestData[];
}
export interface FlightList {
  id: number;
  airlineCode: string;
  flightNumber: string;
  slacCount: string;
  slacWeight: string;
  arrivalDate: string;
  mawbPart: string;
  isInputRow: boolean;
  notes: INotesSectionData;
  source?: string;
  attachments: [];
}
export interface INotesSectionData {
  ibc: INotesDetail[];
  customer: INotesDetail[];
}
export interface INotesDetail {
  id: string | number;
  comments: string;
  action: string;
  isFromScreen?: string;
  notesId?: string | null;
  userId?: 0;
  userName?: string;
  facility?: string;
  date?: string;
  dateTime?: string;
  type?: string;
  source?: string;
  isEditable?: boolean;
  isDisable?: boolean;
  isSaved?: boolean;
}
export interface FlightStateModel {
  flights: FlightList[];
}
export interface IMawbManifestListData {
  id?: any;
  manifestsMawb?: any;
  source?: any;
  manifest?: any;
  manifestId?: string;
  manifestNumber?: string;
  date?: string;
  bags?: number;
  shipments?: number;
  isInputRow?: boolean;
}
export interface IMawbNotesDetails {
  id: string;
  comments: string;
  action: string;
  userId: number;
  userName: string;
}
export interface IMawbAttachmentData {
  id: any;
  type: string;
  fileName: string;
  signedUrl: string;
  action: string;
  attachmentTypeValuId: string;
  isFromScreen?: string;
}
export interface IMawbNotesData {
  ibc: IMawbNotesDetails[];
  customer: IMawbNotesDetails[];
}
export interface IMawbInformationData {
  mawbDate: string;
  /** PAC-24812 — true when MAWB is part of the international incoming flow */
  incomingInternational?: boolean;
  /** PAC-24812 — true when a published Manifest blocks deleting this record */
  isManifestPublished?: boolean;
  mawbId: number;
  mawbExt: string;
  originAirport: number;
  destinationAirport: number;
  airlineId: any;
  airlineName: string;
  information: string;
  receivingAgent: any;
  clearanceType: string;
  agentCode: number;
  agentName: string;
  clearanceTypeCode: string;
  destinationID: string;
  originID: string;
  notes: IMawbNotesData;
  attachmentList: IMawbAttachmentData[];
  miscellaneous: IMawbMiscellaneousData;
  eventsList: IMawbEventData[];
  manifestList: IMawbLinkedManifestData[];
  mawbInfo?: any;
  flights: any;
  flightMiscellaneous?: IFlightMiscellaneousData[];
}

export interface IMawbEventData {
  id: string;
  date: string;
  time: string;
  info: string;
  zone: string;
  name: string;
}

export interface IMawbLinkedManifestData {
  id: string;
  action: string;
  manifestNumber: number;
  mawb?: any;
}
export interface MawbBasicSearchResultsPayload {
  statusCode: number;
  message: string;
  data: {
    totalRecords: number;
    sortingOrder: string;
    sortingField: string;
    mawb: MawbColumnsData[];
  };
}

export interface MawbInfoResetStateModel {
  data: IMawbInformationData;
}

export interface IFlightMiscellaneousData {
  cargoOperator?: any;
  firstUsAirport?: string;
  firstUsAirportDate?: any;
  cargoOperatorName?: string;
  firstUsAirportName?: string;
  action?: string;
  id: any;
  flightId: any;
  isSavedData: boolean;
}

/** `flights[].miscellaneous` shape accepted by MAWB save/update APIs. */
export interface IFlightMiscellaneousApiPayload {
  cargoOperator: number | null;
  firstUsAirport: number | null;
  firstUsAirportDate: string | Date | null;
  cargoOperatorName?: string;
  firstUsAirportName?: string;
}

export interface IFlightMiscellaneousStateModel {
  flightMiscellaneous: IFlightMiscellaneousData[];
}

export interface MawbDropdownList {
  origins: IDropdownResponse[];
  destinations: IDropdownResponse[];
  agentData: IDropdownResponse[];
  clearanceTypeData: IDropdownResponse[];
  attachmentType: IDropdownResponse[];
  airlineData: IDropdownResponse[];
}

export interface IPalletGrid {
  dimL: string;
  dimW: string;
  dimH: string;
  dimensionalFactor: string;
  dimensionalWeight: string;
  actualWeight: string;
  id: number;
  action: string;
  tempId: string;
  isRowDisable: boolean;
}
export interface FlightTimelineEventPayload {
  action?: unknown;
  eventAction?: unknown;
  eventType?: unknown;
  name?: unknown;
  info?: FlightTimelineEventInfoEnvelope;
  [key: string]: unknown;
}

export interface FlightTimelineEventInfoEnvelope {
  data?: unknown;
  flights?: unknown[];
  [key: string]: unknown;
}

export interface TimelineEventDescriptionRow {
  message: string;
  details: Record<string, unknown>;
}
